/**
 * The adapter. Layer A to layer B, as a pure function.
 *
 * Three things happen here and each one is a place a hand written client goes
 * wrong.
 *
 *   1. **Unnesting.** A wire frame is `{ event, data }` and the discriminant is
 *      `event`. A layer B event is flat and the discriminant is `type`. The
 *      payload fields are lifted out of `data` and onto the event itself.
 *   2. **Case translation.** `stream_id` becomes `streamId`,
 *      `estimated_wait_seconds` becomes `estimatedWaitSeconds`. `usage` is the
 *      one exception: its four fields stay snake_case, because that is how
 *      `UsageEvent` is published. A test pins that asymmetry.
 *   3. **Fan out and fan in.** The wire `status` event maps to either
 *      `response.queued` or `response.status` depending on its state, and
 *      `response.status` is also reached from the wire `thinking` event. The
 *      two wire replace events both map to `response.output_text.replace` and
 *      stay distinguishable by payload.
 *
 * Fifteen wire events in. Fourteen published layer B members plus one marked
 * extension out. The sixteenth published member, `response.cursor`, has no wire
 * event: the state machine synthesises it from the `id:` line once the payload
 * has been applied, and a test asserts the adapter never emits it.
 *
 * The function returns `null` rather than throwing for anything a conforming
 * client drops: keepalives, the `[DONE]` sentinel, malformed frames, `message`
 * frames with no event name, unrecognised event names, and recognised events
 * whose required fields are missing or the wrong type. Returning null is what
 * makes the client forward compatible. A server that grows a sixteenth event
 * does not break a client built against fifteen.
 */

import type { DecodedFrame } from './codec.ts';
import { isChatErrorCode, type MatildaChatStreamEvent } from './normalized.ts';
import { isWireEventName, WIRE_DEFAULTS } from './wire.ts';

/** A JSON object, or null when the value is anything else. Arrays are not objects here. */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** The field when it is a string, otherwise undefined. */
function str(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

/** `Number()` coercion with a fallback, matching `Number(x) || 0` in the reference. */
function numOr(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isNaN(n) ? fallback : n;
}

/**
 * Every member the adapter can produce: the published union minus the
 * synthesised `response.cursor`, which has no wire event. Narrowing the return
 * type makes "the adapter never emits the cursor" a fact the compiler holds
 * rather than only a test, and it lets a consumer switch over the result
 * exhaustively without writing an unreachable case.
 */
export type WireBackedEvent = Exclude<MatildaChatStreamEvent, { type: 'response.cursor' }>;

/**
 * Map one decoded frame onto the layer B union, or drop it.
 */
export function toNormalized(frame: DecodedFrame): WireBackedEvent | null {
  if (frame.kind !== 'event') return null;
  if (!isWireEventName(frame.event)) return null;

  // The payload is nested under `data` on the wire. Everything below reads out
  // of this record and writes onto a flat event.
  const record = asRecord(frame.data);

  switch (frame.event) {
    case 'stream_init': {
      if (record === null) return null;
      // Case translation: stream_id becomes streamId.
      const streamId = str(record, 'stream_id');
      // An empty stream id is as useless as a missing one, and the reference
      // drops both for the same reason.
      if (streamId === undefined || streamId === '') return null;
      return { type: 'response.created', streamId };
    }

    case 'token': {
      if (record === null) return null;
      const delta = str(record, 'content');
      // The field is `content`, not `delta`. An empty or missing chunk is
      // dropped: the reference dispatches only when a chunk is present, and a
      // client that appends an empty string still fires a render for nothing.
      if (delta === undefined || delta === '') return null;
      return { type: 'response.output_text.delta', delta };
    }

    case 'thinking': {
      // Declared with a required `content` field, but the reference reads none
      // of it. The payload is deliberately not inspected, not even to check it
      // is an object, so a `thinking` frame with an odd payload still moves the
      // status. This is one of the two ways to reach response.status.
      return { type: 'response.status', status: 'thinking' };
    }

    case 'generation_status': {
      if (record === null) return null;
      const phase = str(record, 'phase');
      if (phase === undefined || phase === '') return null;
      return { type: 'response.generation_status', phase };
    }

    case 'tool_start': {
      if (record === null) return null;
      const tool = str(record, 'tool');
      if (tool === undefined || tool === '') return null;

      const input = str(record, 'input');

      // `args` may arrive as an object, or as a JSON encoded string that has to
      // be parsed. A string that does not parse, or that parses to something
      // other than an object, is dropped rather than passed along.
      let args: Record<string, unknown> | undefined;
      const rawArgs = record['args'];
      const objectArgs = asRecord(rawArgs);
      if (objectArgs !== null) {
        args = objectArgs;
      } else if (typeof rawArgs === 'string') {
        try {
          args = asRecord(JSON.parse(rawArgs)) ?? undefined;
        } catch {
          args = undefined;
        }
      }

      // Precedence: `input` over `args`, `output` over `result`.
      const inputOrArgs = input ?? args;
      const output = str(record, 'output') ?? str(record, 'result');

      return {
        type: 'response.tool_call.started',
        tool,
        ...(inputOrArgs === undefined ? {} : { inputOrArgs }),
        ...(output === undefined ? {} : { output }),
      };
    }

    case 'tool_progress': {
      if (record === null) return null;
      const tool = str(record, 'tool');
      const message = str(record, 'message');
      // Both required. The frame is dropped if either is missing, because a
      // progress line with no text and no tool tells the reader nothing.
      if (tool === undefined || message === undefined) return null;
      return { type: 'response.tool_call.progress', tool, message };
    }

    case 'tool_result': {
      if (record === null) return null;
      const tool = str(record, 'tool');
      if (tool === undefined) return null;

      // Declared as an enum of two values, but treated as success only when it
      // is exactly the string `success`. Everything else, including a missing
      // field and including the string `Success`, is an error. Defaulting the
      // other way would report a failed tool call as a successful one.
      const status = record['status'] === 'success' ? 'success' : 'error';
      const input = str(record, 'input');
      const output = str(record, 'output') ?? str(record, 'result');

      return {
        type: 'response.tool_call.completed',
        tool,
        status,
        ...(input === undefined ? {} : { input }),
        ...(output === undefined ? {} : { output }),
      };
    }

    case 'client_tool_call': {
      if (record === null) return null;
      const name = str(record, 'name');
      if (name === undefined || name === '') return null;

      // Defaults to an empty object when absent or not an object, so a caller
      // can destructure it without a guard. Case translation: the wire calls
      // the correlation field `id`, layer B calls it `callId`, because `id` on
      // a flat event would read as the id of the event itself.
      const args = asRecord(record['args']) ?? {};
      const callId = str(record, 'id');

      return {
        type: 'response.client_tool_call',
        name,
        args,
        ...(callId === undefined ? {} : { callId }),
      };
    }

    case 'safety_replace': {
      if (record === null) return null;
      // Precedence: `message` over `content`. Note the field flip. The wire
      // prefers a field called `message`, layer B carries it as `content`.
      const content = str(record, 'message') ?? str(record, 'content');
      const rawCategories = record['categories'];
      // Filtered to strings, so one bad entry does not poison the list, and
      // defaulted to empty so the notice can always render a category count.
      const categories = Array.isArray(rawCategories)
        ? rawCategories.filter((c): c is string => typeof c === 'string')
        : [];

      return {
        type: 'response.output_text.replace',
        categories,
        ...(content === undefined ? {} : { content }),
      };
    }

    case 'replace': {
      // A bare clear. Distinct from `safety_replace` on the wire, and it stays
      // distinguishable here: no `content` key at all, and an empty category
      // list, so the UI shows a cleared message and no safety categories. The
      // reducer treats this as a full clear rather than a no op.
      return { type: 'response.output_text.replace', categories: [] };
    }

    case 'usage': {
      if (record === null) return null;
      // The one place case is NOT translated. UsageEvent is published with
      // snake_case field names and matching the published shape exactly is the
      // point. There is a test pinning this, because it looks like an
      // oversight next to streamId and estimatedWaitSeconds.
      //
      // The three optional fields stay undefined when absent rather than
      // becoming zero, so the UI can tell "no context data" from "zero percent
      // of context used".
      const contextPct = record['context_pct'];
      const trimmed = record['context_messages_trimmed'];
      const budget = record['context_budget_tokens'];

      return {
        type: 'response.usage',
        usage: {
          output_tokens: numOr(record['output_tokens'], WIRE_DEFAULTS.outputTokens),
          ...(contextPct == null ? {} : { context_pct: Number(contextPct) }),
          ...(trimmed == null ? {} : { context_messages_trimmed: Number(trimmed) }),
          ...(budget == null ? {} : { context_budget_tokens: Number(budget) }),
        },
      };
    }

    case 'status': {
      if (record === null) return null;
      const state = str(record, 'state') ?? WIRE_DEFAULTS.queueState;

      // The fan out. A queued state carries a position and a wait, and the UI
      // renders a queue notice from it. Any other state is a plain status
      // change and carries neither, so sending it as response.queued would put
      // "position 0" in front of a reader who is not in a queue at all.
      if (state !== WIRE_DEFAULTS.queueState) {
        return { type: 'response.status', status: state };
      }

      return {
        type: 'response.queued',
        state,
        position: numOr(record['position'], WIRE_DEFAULTS.queueNumber),
        // Case translation: estimated_wait_seconds becomes estimatedWaitSeconds.
        estimatedWaitSeconds: numOr(record['estimated_wait_seconds'], WIRE_DEFAULTS.queueNumber),
      };
    }

    case 'truncated': {
      if (record === null) return null;
      // Defaulted in the schema and defaulted again in the parser. An empty
      // string is treated as absent, which is the reference behaviour.
      const reason = str(record, 'reason');
      return {
        type: 'response.truncated',
        reason: reason === undefined || reason === '' ? WIRE_DEFAULTS.truncatedReason : reason,
      };
    }

    case 'done': {
      // The four declared fields are all optional and all ignored, so the
      // payload is not inspected. Note the rename: the wire says `done`, layer
      // B says `response.completed`.
      return { type: 'response.completed' };
    }

    case 'error': {
      // The wire message field is named `error`, not `message`.
      if (record === null) {
        return { type: 'response.error', code: 'unknown', message: JSON.stringify(frame.data) };
      }
      const rawCode = record['code'];
      const code = typeof rawCode === 'string' && isChatErrorCode(rawCode) ? rawCode : 'unknown';
      // The reference falls back to the raw data line. The raw line is gone by
      // the time the frame reaches here, so the payload is re-serialised, which
      // is the same text for every payload the codec accepted.
      const message = str(record, 'error') ?? JSON.stringify(record);
      // retryAfterSeconds is deliberately absent. The wire carries no such
      // field; retry timing is client state derived from a Retry-After header
      // and it lives in StreamState.error.
      return { type: 'response.error', code, message };
    }
  }
}
